import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DsIconComponent } from '../icons/ds-icon.component';

export type DsInputState = 'default' | 'error';
export type DsInputType  = 'text' | 'email' | 'tel' | 'number' | 'url' | 'search';
export type DsInputVisualTransformation = 'none' | 'rut' | 'card' | 'phone' | 'password' | 'thousands';

@Component({
  selector: 'ds-input-text',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ds-input" [class.ds-input--error]="state === 'error'" [class.ds-input--disabled]="disabled"
         [attr.aria-disabled]="disabled || null">

      @if (label || hasLabelTrailingContent) {
        <div class="ds-input__label-row">
          @if (label) {
            <label class="ds-input__label" [attr.for]="inputId">{{ label }}</label>
          }
          <span class="ds-input__label-trailing">
            <ng-content select="[labelTrailingContent]"></ng-content>
          </span>
        </div>
      }

      <div class="ds-input__field-box" (click)="focusInput()">
        @if (showPrefix && prefix) {
          <span class="ds-input__prefix" aria-hidden="true">{{ prefix }}</span>
        }

        <input
          #inputControl
          class="ds-input__input"
          [attr.id]="inputId"
          [type]="inputType"
          [placeholder]="placeholder"
          [value]="displayValue"
          [disabled]="disabled"
          [attr.maxlength]="nativeMaxLength"
          [attr.autocomplete]="autocomplete || null"
          [attr.aria-invalid]="state === 'error' ? 'true' : null"
          [attr.aria-describedby]="showHelpText && helpText ? helpId : null"
          (input)="onInput($event)"
          (focus)="focused.emit($event)"
          (blur)="blurred.emit($event)"
        />

        @if (state === 'error') {
          <span class="ds-input__error-icon" aria-hidden="true">
            <ds-icon name="error" size="20px" [fill]="true"></ds-icon>
          </span>
        }
      </div>

      @if (showHelpText || showCounter) {
        <div class="ds-input__help-area">
          @if (showHelpText) {
            <span class="ds-input__help-text" [attr.id]="helpId"
                  [attr.role]="state === 'error' ? 'alert' : null">{{ helpText }}</span>
          }
          @if (showCounter) {
            <span class="ds-input__counter" aria-live="polite">{{ value.length }}/{{ maxCharacters }}</span>
          }
        </div>
      }

    </div>
  `,
  styles: [`
    :host { display: block; }

    /* ── Container ───────────────────────────────────────────── */
    .ds-input {
      display:        flex;
      flex-direction: column;
      gap:            var(--ds-input-gap);
      min-width:      200px;
    }

    .ds-input--disabled {
      opacity:        0.44;
      pointer-events: none;
    }

    /* ── Label ───────────────────────────────────────────────── */
    .ds-input__label-row {
      display:     flex;
      align-items: center;
      gap:         var(--ds-input-prefix-gap);
    }

    .ds-input__label {
      flex: 0 1 auto;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size:   var(--ds-input-label-font-size);
      line-height: var(--ds-input-label-line-height);
      color:       var(--ds-input-label-color);
    }

    .ds-input__label-trailing {
      display:     inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    .ds-input__label-trailing:empty { display: none; }

    /* ── Field box ───────────────────────────────────────────── */
    .ds-input__field-box {
      display:       flex;
      align-items:   center;
      gap:           var(--ds-input-prefix-gap);
      height:        var(--ds-input-field-height);
      padding:       var(--ds-input-padding-y) var(--ds-input-padding-x);
      background:    var(--ds-input-background);
      border:        var(--ds-input-border-width) solid var(--ds-input-border-color);
      border-radius: var(--ds-input-border-radius);
      position:      relative;
      transition:    border-color 120ms ease;
      box-sizing:    border-box;
      cursor:        text;
    }

    .ds-input--error .ds-input__field-box {
      background:   var(--ds-input-background-error);
      border-color: var(--ds-input-border-color-error);
    }

    .ds-input__field-box:focus-within {
      outline:        var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    /* ── Prefix ──────────────────────────────────────────────── */
    .ds-input__prefix {
      font-family:  'Sura Sans', system-ui, sans-serif;
      font-weight:  400;
      font-size:    var(--ds-input-field-font-size);
      color:        var(--ds-input-prefix-color);
      white-space:  nowrap;
      flex-shrink:  0;
      line-height:  var(--ds-input-field-line-height);
    }

    /* ── Native input ────────────────────────────────────────── */
    .ds-input__input {
      flex:        1 1 0;
      min-width:   0;
      width:       100%;
      height:      100%;
      border:      none;
      outline:     none;
      background:  transparent;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size:   var(--ds-input-field-font-size);
      line-height: var(--ds-input-field-line-height);
      color:       var(--ds-input-value-color);
      caret-color: var(--ds-input-cursor-color);
      padding:     0;
    }

    .ds-input__input::placeholder { color: var(--ds-input-placeholder-color); }

    /* ── Error icon ──────────────────────────────────────────── */
    .ds-input__error-icon {
      display:     flex;
      align-items: center;
      flex-shrink: 0;
      color:       var(--ds-input-error-icon-color);
    }

    /* ── Help area ───────────────────────────────────────────── */
    .ds-input__help-area {
      display: flex;
      gap:     var(--ds-input-help-gap);
      width:   100%;
    }

    .ds-input__help-text {
      flex:        1 0 0;
      min-width:   0;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size:   var(--ds-input-help-font-size);
      line-height: var(--ds-input-help-line-height);
      color:       var(--ds-input-help-color);
    }

    .ds-input--error .ds-input__help-text {
      color: var(--ds-input-help-color-error);
    }

    .ds-input__counter {
      margin-left:  auto;
      font-family:  'Sura Sans', system-ui, sans-serif;
      font-weight:  400;
      font-size:    var(--ds-input-help-font-size);
      line-height:  var(--ds-input-help-line-height);
      color:        var(--ds-input-counter-color);
      white-space:  nowrap;
      flex-shrink:  0;
    }
  `],
})
export class DsInputTextComponent {
  private static _counter = 0;

  @ViewChild('inputControl') private inputControl?: ElementRef<HTMLInputElement>;

  readonly inputId = `ds-input-text-${++DsInputTextComponent._counter}`;
  readonly helpId  = `${this.inputId}-help`;

  @Input() value:         string         = '';
  @Input() label:         string         = '';
  @Input() placeholder:   string         = '';
  @Input() prefix:        string         = '';
  @Input() showPrefix:    boolean        = false;
  @Input() helpText:      string         = '';
  @Input() showHelpText:  boolean        = false;
  @Input() maxCharacters: number | null  = null;
  @Input() showMaxCharacters: boolean    = true;
  @Input() formatAsNumericAmount: boolean = false;
  @Input() state:         DsInputState   = 'default';
  @Input() disabled:      boolean        = false;
  @Input() type:          DsInputType    = 'text';
  @Input() visualTransformation: DsInputVisualTransformation = 'none';
  @Input() autocomplete:  string         = '';
  @Input() hasLabelTrailingContent: boolean = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() focused     = new EventEmitter<FocusEvent>();
  @Output() blurred     = new EventEmitter<FocusEvent>();

  get showCounter(): boolean {
    return this.maxCharacters !== null && this.showMaxCharacters;
  }

  get inputType(): string {
    return this.visualTransformation === 'password' ? 'password' : this.type;
  }

  get nativeMaxLength(): number | null {
    return this.visualTransformation === 'none' ? this.maxCharacters : null;
  }

  get displayValue(): string {
    return this.formatDisplayValue(this.value);
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let nextValue = this.parseDisplayValue(input.value);

    if (this.formatAsNumericAmount) {
      nextValue = nextValue.replace(/^0+/, '');
      if (!nextValue && input.value) {
        nextValue = '0';
      }
    }

    if (this.maxCharacters !== null) {
      nextValue = nextValue.slice(0, this.maxCharacters);
    }

    if (input.value !== nextValue) {
      input.value = this.formatDisplayValue(nextValue);
    }

    this.value = nextValue;
    this.valueChange.emit(nextValue);
  }

  focusInput(): void {
    if (!this.disabled) {
      this.inputControl?.nativeElement.focus();
    }
  }

  private parseDisplayValue(displayValue: string): string {
    if (this.visualTransformation === 'rut') {
      return displayValue.replace(/[^0-9kK]/g, '').toUpperCase();
    }

    if (
      this.visualTransformation === 'card' ||
      this.visualTransformation === 'phone' ||
      this.visualTransformation === 'thousands'
    ) {
      return displayValue.replace(/\D+/g, '');
    }

    return displayValue;
  }

  private formatDisplayValue(rawValue: string): string {
    if (this.visualTransformation === 'rut') {
      return this.formatRut(rawValue);
    }

    if (this.visualTransformation === 'card') {
      return rawValue.replace(/\D+/g, '').replace(/(.{4})/g, '$1 ').trim();
    }

    if (this.visualTransformation === 'phone') {
      const digits = rawValue.replace(/\D+/g, '').slice(0, 11);
      if (digits.length <= 2) return digits;
      if (digits.length <= 3) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
      if (digits.length <= 7) return `+${digits.slice(0, 2)} ${digits.slice(2, 3)} ${digits.slice(3)}`;
      return `+${digits.slice(0, 2)} ${digits.slice(2, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
    }

    if (this.visualTransformation === 'thousands') {
      return this.groupDigits(rawValue.replace(/\D+/g, ''));
    }

    return rawValue;
  }

  private formatRut(rawValue: string): string {
    const cleanValue = rawValue.replace(/[^0-9kK]/g, '').toUpperCase();
    if (cleanValue.length <= 1) return cleanValue;

    const body = cleanValue.slice(0, -1);
    const verifier = cleanValue.slice(-1);
    const formattedBody = this.groupDigits(body);

    return `${formattedBody}-${verifier}`;
  }

  private groupDigits(digits: string): string {
    const groups: string[] = [];

    for (let end = digits.length; end > 0; end -= 3) {
      groups.unshift(digits.slice(Math.max(0, end - 3), end));
    }

    return groups.join('.');
  }
}
